import { useEffect, useRef, useState } from 'react';
import { motion, useSpring, useTransform, AnimatePresence } from 'framer-motion';

// =============================================================================
// ANIMATED NUMBER (Count-up effect)
// =============================================================================

interface AnimatedNumberProps {
  value: number;
  duration?: number;
  formatOptions?: Intl.NumberFormatOptions;
  prefix?: string;
  suffix?: string;
  className?: string;
  style?: React.CSSProperties;
  showChange?: boolean; // Show +/- indicator on change
  highlightChange?: boolean; // Flash green/red on change
}

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 0.8,
  formatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  prefix = '',
  suffix = '',
  className,
  style,
  showChange = false,
  highlightChange = true,
}) => {
  const prevValue = useRef(value);
  const [changeDirection, setChangeDirection] = useState<'up' | 'down' | null>(null);

  const spring = useSpring(value, {
    stiffness: 100,
    damping: 30,
    duration: duration * 1000,
  });

  const display = useTransform(spring, (current) =>
    new Intl.NumberFormat('en-US', formatOptions).format(current)
  );

  useEffect(() => {
    if (value !== prevValue.current) {
      const direction = value > prevValue.current ? 'up' : 'down';
      setChangeDirection(direction);

      // Reset change indicator after animation
      const timer = setTimeout(() => setChangeDirection(null), 1500);
      prevValue.current = value;

      return () => clearTimeout(timer);
    }
    spring.set(value);
  }, [value, spring]);

  const highlightColor = changeDirection === 'up'
    ? 'rgba(34, 197, 94, 0.3)'
    : changeDirection === 'down'
      ? 'rgba(239, 68, 68, 0.3)'
      : 'transparent';

  return (
    <motion.span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        position: 'relative',
        ...style,
      }}
      animate={highlightChange && changeDirection ? {
        backgroundColor: [highlightColor, 'transparent'],
      } : {}}
      transition={{ duration: 0.5 }}
    >
      {prefix}
      <motion.span>{display}</motion.span>
      {suffix}

      {/* Change indicator */}
      <AnimatePresence>
        {showChange && changeDirection && (
          <motion.span
            initial={{ opacity: 0, y: changeDirection === 'up' ? 10 : -10, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: changeDirection === 'up' ? -10 : 10 }}
            style={{
              color: changeDirection === 'up' ? '#22c55e' : '#ef4444',
              fontSize: '0.75em',
              fontWeight: 'bold',
              marginLeft: 4,
            }}
          >
            {changeDirection === 'up' ? '↑' : '↓'}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );
};

// =============================================================================
// ANIMATED CURRENCY
// =============================================================================

interface AnimatedCurrencyProps {
  value: number;
  currency?: string;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
  compact?: boolean;
  showChange?: boolean;
}

export const AnimatedCurrency: React.FC<AnimatedCurrencyProps> = ({
  value,
  currency = 'USD',
  duration = 0.8,
  className,
  style,
  compact = false,
  showChange = false,
}) => {
  const formatOptions: Intl.NumberFormatOptions = compact
    ? {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      }
    : {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };

  return (
    <AnimatedNumber
      value={value}
      duration={duration}
      formatOptions={formatOptions}
      className={className}
      style={style}
      showChange={showChange}
    />
  );
};

// =============================================================================
// ANIMATED PERCENTAGE
// =============================================================================

interface AnimatedPercentageProps {
  value: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
  showSign?: boolean;
  colorCode?: boolean; // Green for positive, red for negative
}

export const AnimatedPercentage: React.FC<AnimatedPercentageProps> = ({
  value,
  duration = 0.8,
  className,
  style,
  showSign = true,
  colorCode = true,
}) => {
  const prevValue = useRef(value);
  const [isIncreasing, setIsIncreasing] = useState(false);

  useEffect(() => {
    if (value > prevValue.current) {
      setIsIncreasing(true);
    }
    prevValue.current = value;
  }, [value]);

  const color = colorCode
    ? value > 0
      ? '#22c55e'
      : value < 0
        ? '#ef4444'
        : 'inherit'
    : 'inherit';

  const prefix = showSign && value > 0 ? '+' : '';

  return (
    <motion.span
      className={className}
      style={{
        color,
        fontWeight: 500,
        ...style,
      }}
      animate={isIncreasing ? { scale: [1, 1.1, 1] } : {}}
      onAnimationComplete={() => setIsIncreasing(false)}
    >
      <AnimatedNumber
        value={value}
        duration={duration}
        formatOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        prefix={prefix}
        suffix="%"
        highlightChange={false}
      />
    </motion.span>
  );
};

// =============================================================================
// SLOT MACHINE NUMBER (Rolling digits effect)
// =============================================================================

interface SlotMachineNumberProps {
  value: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

const SingleDigit: React.FC<{ digit: string; duration: number }> = ({ digit, duration }) => {
  const digitIndex = DIGITS.indexOf(digit);

  return (
    <div
      style={{
        height: '1.2em',
        overflow: 'hidden',
        display: 'inline-block',
      }}
    >
      <motion.div
        animate={{ y: `-${digitIndex * 1.2}em` }}
        transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20,
          duration,
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {DIGITS.map((d) => (
          <span
            key={d}
            style={{
              height: '1.2em',
              lineHeight: '1.2em',
            }}
          >
            {d}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

export const SlotMachineNumber: React.FC<SlotMachineNumberProps> = ({
  value,
  duration = 0.5,
  className,
  style,
}) => {
  const valueString = Math.abs(Math.round(value)).toString();
  const isNegative = value < 0;

  return (
    <span className={className} style={{ display: 'inline-flex', ...style }}>
      {isNegative && <span>-</span>}
      {valueString.split('').map((digit, index) => (
        <SingleDigit key={index} digit={digit} duration={duration + index * 0.05} />
      ))}
    </span>
  );
};

// =============================================================================
// VALUE FLASH (Highlight on change)
// =============================================================================

interface ValueFlashProps {
  children: React.ReactNode;
  trigger: unknown; // Any value that changes
  flashColor?: string;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const ValueFlash: React.FC<ValueFlashProps> = ({
  children,
  trigger,
  flashColor = 'rgba(255, 215, 0, 0.4)',
  duration = 0.5,
  className,
  style,
}) => {
  const [flash, setFlash] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), duration * 1000);
    return () => clearTimeout(timer);
  }, [trigger, duration]);

  return (
    <motion.span
      className={className}
      style={{
        display: 'inline-block',
        borderRadius: 4,
        padding: '0 4px',
        ...style,
      }}
      animate={{
        backgroundColor: flash ? flashColor : 'transparent',
        scale: flash ? [1, 1.05, 1] : 1,
      }}
      transition={{ duration }}
    >
      {children}
    </motion.span>
  );
};

// =============================================================================
// TRENDING INDICATOR
// =============================================================================

interface TrendingIndicatorProps {
  value: number;
  previousValue: number;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const TrendingIndicator: React.FC<TrendingIndicatorProps> = ({
  value,
  previousValue,
  showPercentage = true,
  size = 'md',
  className,
}) => {
  const percentChange = previousValue !== 0
    ? ((value - previousValue) / Math.abs(previousValue)) * 100
    : 0;
  const isUp = value > previousValue;
  const isDown = value < previousValue;

  const sizeStyles = {
    sm: { fontSize: '0.75rem', iconSize: 12 },
    md: { fontSize: '0.875rem', iconSize: 16 },
    lg: { fontSize: '1rem', iconSize: 20 },
  };

  const { fontSize, iconSize } = sizeStyles[size];

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize,
        color: isUp ? '#22c55e' : isDown ? '#ef4444' : '#888',
        fontWeight: 500,
      }}
    >
      {/* Arrow icon */}
      <motion.svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={{
          rotate: isUp ? 0 : isDown ? 180 : 90,
          y: isUp || isDown ? [0, -2, 0] : 0,
        }}
        transition={{
          rotate: { type: 'spring', stiffness: 300 },
          y: { repeat: Infinity, duration: 1.5, repeatType: 'reverse' },
        }}
      >
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </motion.svg>

      {/* Percentage */}
      {showPercentage && (
        <AnimatedPercentage value={percentChange} colorCode={false} showSign />
      )}
    </motion.div>
  );
};

export default {
  AnimatedNumber,
  AnimatedCurrency,
  AnimatedPercentage,
  SlotMachineNumber,
  ValueFlash,
  TrendingIndicator,
};
